import React, { useState, useEffect } from "react";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import PropTypes from "prop-types";

import { createInternalTheme } from '@pixel/internal/styles';
import { ThemeProvider } from "@pixel/core";

import SignDocument, { OnSignData } from './SignDocument';
import DocumentViewer from "./DocumentViewer";

import { LocalizationProvider } from "../contexts/Localization/LocalizationProvider";
import { LocalizationValues } from "../contexts/Localization/LocalizationTypes";

import { useMultipleLocalizedText } from '../contexts/Localization/localizationHooks';

const theme = createInternalTheme({ darkMode: false })

const checkboxStyle = {
    marginLeft: 11
}

export const App = (props: InjectedProps) => {
    const [openConsentDialog, setOpenConsentDialog] = React.useState(false);
    const [isChecked, setIsChecked] = React.useState(false);
    const [signatureModalVisible, setSignatureModalVisible] = React.useState(false);
    const [signatureLoading, setSignatureLoading] = React.useState(false);
    const [signatureError, setSignatureError] = React.useState('');

    const handleOpenConsentDialog = () => {
        return props.getConsent && props.consentDialog;
    };

    const handleCloseConsentDialog = () => {
      setOpenConsentDialog(false);
    };

    const handleRejectConsentDialog = () => {
      setIsChecked(false);
      props.onReject();
      props.closeOfferLetter();
    };

    const handleConsentDialog = () => {
      setIsChecked(false);
      props.onConsent();
    };

    const handleChecked = (event: any) => {
        setIsChecked(event.target.checked);
    }

    const handleOpenSignatureModal = () => {
      setSignatureModalVisible(true);
    };

  return (
    <React.StrictMode>
      <ThemeProvider theme={theme}>
          <LocalizationProvider
            locale={(props.locale || 'en-us').toLowerCase()}
            translations={props.translations as LocalizationValues}
          >
            <SignDocument
              open={signatureModalVisible}
              loading={signatureLoading}
              error={signatureError}
              firstName={props.firstName}
              lastName={props.lastName}
              onClose={() => {
                setSignatureModalVisible(false)
              }}
              onSign={(e, data) => {
                setSignatureLoading(true)
                setSignatureError('')
                props.onSaveSignature(data)
                  .then((res: any) => {
                    setSignatureLoading(false)
                    if (res.status == 200 && res.data && res.data.status === 'COMPLETED') {
                      setSignatureModalVisible(false);
                      props.closeOfferLetter();
                    } else {
                      setSignatureError(props.translations.ERROR_SIGNATUREGENERATION)
                    }
                  })
                  .catch((err) => {
                    setSignatureLoading(false)
                    setSignatureError(props.translations.ERROR_SIGNATUREGENERATION)
                  })
              }}
            />
           <DocumentViewer
              open={props.documentViewer.visible}
              documentURL={props.documentURL}
              type = {props.documentViewer.document.type}
              onClose={() => { 
                  props.closeOfferLetter();
                }
              }
              onSign = {handleOpenSignatureModal}
            />
            <Dialog
              open={handleOpenConsentDialog()}
              onClose={handleCloseConsentDialog}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title" sx={{color: 'rgb(0 0 0 / 85%)'}}>
                {props.translations.NATIVEESIGNELECTRONICCONSENTAGREEMENTTITLE}
              </DialogTitle>
              <DialogContent sx={{paddingBottom: 0}}>
                <DialogContentText id="alert-dialog-description" sx={{color: 'rgb(0 0 0 / 85%)'}}>
                    <b><u>{props.translations.NATIVE_ESIGN_ELECTRONIC_CONSENT_AGREEMENT_MAIN_HEADER}</u></b><br />
                    {props.translations.NATIVE_ESIGN_ELECTRONIC_CONSENT_AGREEMENT_MAIN_BODY}<br />
                    <b><u>{props.translations.NATIVE_ESIGN_ELECTRONIC_CONSENT_AGREEMENT_SECTION_1_HEADER}</u></b>
                    &nbsp;{props.translations.NATIVE_ESIGN_ELECTRONIC_CONSENT_AGREEMENT_SECTION_1_BODY}<br />
                    <b><u>{props.translations.NATIVE_ESIGN_ELECTRONIC_CONSENT_AGREEMENT_SECTION_2_HEADER}</u></b>
                    &nbsp;{props.translations.NATIVE_ESIGN_ELECTRONIC_CONSENT_AGREEMENT_SECTION_2_BODY}<br />
                    <b><u>{props.translations.NATIVE_ESIGN_ELECTRONIC_CONSENT_AGREEMENT_SECTION_3_HEADER}</u></b>
                    &nbsp;{props.translations.NATIVE_ESIGN_ELECTRONIC_CONSENT_AGREEMENT_SECTION_3_BODY}<br />
                    <b><u>{props.translations.NATIVE_ESIGN_ELECTRONIC_CONSENT_AGREEMENT_SECTION_4_HEADER}</u></b>
                    &nbsp;{props.translations.NATIVE_ESIGN_ELECTRONIC_CONSENT_AGREEMENT_SECTION_4_BODY}<br />
                    <b><u>{props.translations.NATIVE_ESIGN_ELECTRONIC_CONSENT_AGREEMENT_SECTION_5_HEADER}</u></b>
                    &nbsp;{props.translations.NATIVE_ESIGN_ELECTRONIC_CONSENT_AGREEMENT_SECTION_5_BODY}<br />
                    <b><u>{props.translations.NATIVE_ESIGN_ELECTRONIC_CONSENT_AGREEMENT_SECTION_6_HEADER}</u></b>
                    &nbsp;{props.translations.NATIVE_ESIGN_ELECTRONIC_CONSENT_AGREEMENT_SECTION_6_BODY}
                </DialogContentText>
                <FormGroup sx={{position: 'sticky', bottom: 0, backgroundColor: '#fff'}}>
                  <FormControlLabel control={<Checkbox value={isChecked} onChange={handleChecked} />} label={<b>{props.translations.NATIVEESIGNELECTRONICCONSENTAGREEMENTACCEPTANCE}</b>} />
                </FormGroup>
              </DialogContent>

              <DialogActions>
                <Button variant="outlined" onClick={handleRejectConsentDialog}>
                  {props.translations.CANCEL}
                </Button>
                <Button variant="contained" onClick={handleConsentDialog} disabled={!isChecked} autoFocus>
                  {props.translations.CONTINUE}
                </Button>
              </DialogActions>
            </Dialog>
          </LocalizationProvider>
        </ThemeProvider>
    </React.StrictMode>
  );
};

interface InjectedProps {
  firstName: string
  lastName: string
  locale: string
  translations: any
  consentDialog: boolean
  documentViewer: any
  documentURL: string
  getConsent: boolean
  onConsent: () => void
  onReject: () => void
  closeOfferLetter: () => void
  onSaveSignature: (form: OnSignData) => Promise<SaveSignatureSuccessResponse>
}

const InjectedAngularPropTypes = PropTypes.shape({
  $http: PropTypes.func,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  translations: PropTypes.object,
  onSaveSignature: PropTypes.func.isRequired,
  documentViewer: PropTypes.object,
  closeOfferLetter: PropTypes.func.isRequired,
  documentURL: PropTypes.string,
}).isRequired;

App.propTypes = InjectedAngularPropTypes;

export { react2angular } from "../lib/react2Angular";
export default App;
interface SaveSignatureSuccessResponse {
  msgInfo: number
}
interface SaveSignatureErrorResponse {
  msgInfo: number
  error: string
}